import {
  GridColDef,
  GridValueFormatterParams,
  GridValueGetterParams,
} from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { capitalize } from "lodash";

import { NO_SYMBOL } from "@/hooks/useConfig/currencyFormatOptions";
import { Config } from "@/hooks/useConfig";
import { FormatCurrencyFunction } from "@/hooks/useConfig/types";
import { formatPercentage } from "@/pages/scenario-builder/utils";
import { NO_DATA } from "@/pages/scenario-builder/utils/scenarioConstants";
import { GeoViewToggle } from "@/shared/types";
import {
  currencySortComparator,
  dateSortComparator,
  valueSortComparator,
} from "@/shared/utils/columnSort";
import { formatDateLocale } from "@/shared/utils/formatDate";

import { ROIDataPoint } from "../../types/common";

interface GridColumnDefinitionV2 {
  category: string;
  level: string;
}

export type GridColumnDefinition = GridColDef & GridColumnDefinitionV2;

export type FlightingGridRow = {
  [key: string]: string | dayjs.Dayjs | number | null;
};

const getSortComparator = (field: string) => {
  switch (field) {
    case "date":
      return dateSortComparator;
    case "roi":
      return valueSortComparator;
    default:
      return currencySortComparator;
  }
};

export const toGridRows = (
  data: ROIDataPoint[],
  formatCurrency: FormatCurrencyFunction
): FlightingGridRow[] => {
  const rows = data.map((item, index) => {
    const formattedData = Object.entries(item).reduce(
      (acc: { [key: string]: any }, [fieldKey, fieldValue]) => {
        if (fieldKey === "date") {
          acc[fieldKey] = dayjs(fieldValue as string);
        } else if (fieldKey === "geography") {
          acc[fieldKey] = fieldValue;
        } else if (fieldKey === "roi") {
          acc[fieldKey] = item.hideROI ? NO_DATA : formatPercentage(Number(fieldValue) ?? null);
        } else {
          acc[fieldKey] = formatCurrency(Number(fieldValue) ?? null, NO_SYMBOL);
        }
        return acc;
      },
      {}
    );

    return {
      id: index,
      ...formattedData,
    };
  });

  return rows;
};

const createHeaderNameMap = (currencySymbol: string, showReturnInsteadOfRevenue: boolean) => ({
  roi: "Rolling ROI (%)",
  date: "Date",
  totalSpend: `Total Spend (${currencySymbol})`,
  totalRevenue: showReturnInsteadOfRevenue ? `Total Return` : `Total Revenue (${currencySymbol})`,
});

const createColumnDefinition = (key: string, headerNameMap: Record<string, string>) => {
  const baseColumn = {
    field: key,
    headerName: headerNameMap[key] || capitalize(key),
    width: 200,
    sortingOrder: ["desc", "asc"],
  };

  if (key === "date") {
    return {
      ...baseColumn,
      type: "date",
      valueGetter: (params: GridValueGetterParams) => dayjs(params.value),
      valueFormatter: (params: GridValueFormatterParams) => formatDateLocale(dayjs(params.value)),
      sortComparator: getSortComparator(key),
    };
  } else {
    return {
      ...baseColumn,
      sortComparator: (v1: any, v2: any) => getSortComparator(key)(v1, v2),
    };
  }
};

export const toGridColumns = (
  data: ROIDataPoint[],
  currencySymbol: string,
  geoView: GeoViewToggle,
  hasGeoData: boolean,
  config: Config
): GridColDef[] => {
  if (data.length === 0) {
    return [];
  }

  const firstRow = data[0];
  if (!firstRow) {
    throw new Error("Unexpected undefined value for firstRow");
  }

  const headerNameMap = createHeaderNameMap(currencySymbol, config.showReturnInsteadOfRevenue);

  const keys = Object.keys(firstRow)
    .filter((key) => key !== "date" && key !== "roi" && key !== "hideROI")
    .sort();
  const geographyIndex = keys.indexOf("geography");
  if (geographyIndex !== -1) {
    keys.splice(geographyIndex, 1);
  }

  // Only include 'geo' column if view is split AND there is geographies data
  const baseKeys = geoView === GeoViewToggle.SPLIT && hasGeoData ? ["date", "geography"] : ["date"];
  keys.unshift(...baseKeys);
  keys.push("roi");

  const columns = keys.map((key) => createColumnDefinition(key, headerNameMap)) as GridColDef[];

  return columns;
};
